import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Cart, LineItem } from 'shared/types/cart';
import { ExtendedLineItem } from 'shared/types/cart/LineItem';
import { Category, Variant } from 'shared/types/product';
import { ExtendedVariant } from 'shared/types/product/Variant';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Gallery from 'components/commercetools-ui/organisms/gallery';
import { useAddToCartOverlay } from 'context/add-to-cart-overlay';
import {
  CART_LINE_ITEM_ID_QUERY_PARAM,
  CART_ITEM_QUANTITY_QUERY_PARAM,
} from 'helpers/constants/query-params';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import { Reference } from 'types/reference';
import AdditionalInfo from './components/additional-info';
import ProductInformation from './components/product-information';
import { FrontasticImage } from '../../../atoms/image';
import { customQuoteLink } from '../../product/product-list/context/constants';
import { ReviewListProps } from '../../review-list';
import { hasCustomQuoteCta } from '../product-details/helpers';
import { findMatchingLineItem } from '../product-details/helpers/findMatchingLineItem';
import { useFinishAndSheens } from '../product-details/hooks/useFinishAndSheens';
import { UIProduct, UIProductContent } from '../product-details/types';

const getQuantityFromParam = (quantityParam: string | null) => {
  const quantity = Number(quantityParam);
  return Number.isInteger(quantity) && quantity > 0 ? quantity : undefined;
};

export interface CustomProductDetailsProps {
  product: UIProduct;
  productContent?: UIProductContent;
  variant: ExtendedVariant;
  url?: string;
  category?: Category;
  inModalVersion?: boolean;
  woodFinishHelper?: {
    text?: string;
    linkLabel?: string;
    link?: Reference;
  };
  wirebrushModal?: {
    title: string;
    text: string;
    images?: Array<{ name: string; image: FrontasticImage }>;
    closeButtonAriaLabel?: string;
  };
  onChangeVariant: (sku: string) => void;
  description?: ReactNode;
  plainTextDescription?: string;
  origin?: string;
  setIsOpen?: (value: boolean) => void;
  onAddToCart?: (
    variant: Variant,
    quantity: number,
    finish?: string,
    sheen?: string,
    hasWirebrush?: boolean,
    options?: { skipAnalytics?: boolean },
  ) => Promise<Cart>;
  removeLineItem?: (item: LineItem & { customLineItemId?: string }) => Promise<void>;
  updateLineItem?: (
    lineItemId: string,
    customLineItemId: string | undefined,
    quantity: number,
  ) => Promise<{ isError: boolean; data?: Cart; error?: unknown }>;
  editLineItem?: ExtendedLineItem;
  isCartItemEditPending?: boolean;
  reviewListProps?: ReviewListProps;
  categoryCtaText?: string;
  categoryCtaUrl?: string;
}

const CustomProductDetails: FC<CustomProductDetailsProps> = ({
  product,
  productContent,
  variant,
  category,
  url,
  description,
  plainTextDescription,
  inModalVersion,
  onChangeVariant,
  setIsOpen,
  onAddToCart,
  removeLineItem,
  updateLineItem,
  editLineItem,
  isCartItemEditPending = false,
  reviewListProps,
  origin,
  categoryCtaText,
  categoryCtaUrl,
}) => {
  const { formatMessage } = useFormat({ name: 'cart' });
  const router = useRouter();
  const searchParams = useSearchParams();
  const initialQuantity = editLineItem?.count ?? getQuantityFromParam(searchParams.get(CART_ITEM_QUANTITY_QUERY_PARAM)) ?? 1;
  const hasCartItemEditQuery = searchParams.get(CART_LINE_ITEM_ID_QUERY_PARAM) !== null;

  const [quantity, setQuantity] = useState<number>(initialQuantity);
  const [loading, setLoading] = useState(false);
  const [added, setAdded] = useState(false);
  const [hasWirebrush] = useState<boolean>();
  const hasSyncedInitialQuantity = useRef(false);

  useEffect(() => {
    if (hasSyncedInitialQuantity.current) return;
    if (hasCartItemEditQuery && !editLineItem) return;

    setQuantity(initialQuantity);
    hasSyncedInitialQuantity.current = true;
  }, [editLineItem, hasCartItemEditQuery, initialQuantity]);

  const { finish, hasUnresolvedCartFinish, sheen } = useFinishAndSheens(product);

  const { show } = useAddToCartOverlay();
  const isEditingCartItem = !!editLineItem?.lineItemId;

  const isSameCartConfiguration = () => {
    if (!editLineItem) return false;

    const normalizeConfigValue = (value: unknown) => {
      if (value === undefined || value === null || value === '') return undefined;
      return String(value);
    };

    return (
      editLineItem.variant?.sku === variant.sku &&
      normalizeConfigValue(editLineItem.config?.finish) === normalizeConfigValue(finish?.name) &&
      normalizeConfigValue(editLineItem.config?.sheen) === normalizeConfigValue(sheen)
    );
  };

  const handleUpdateCartItem = async () => {
    if (!editLineItem?.lineItemId) return;

    if (isSameCartConfiguration() && updateLineItem) {
      const result = await updateLineItem(editLineItem.lineItemId, editLineItem.customLineItemId, quantity);
      if (result.isError) throw result.error;
    } else if (editLineItem.variant?.sku === variant.sku && removeLineItem) {
      await onAddToCart?.(variant, quantity, finish?.name, sheen, hasWirebrush, { skipAnalytics: true });
      await removeLineItem(editLineItem);
    } else {
      await onAddToCart?.(variant, quantity, finish?.name, sheen, hasWirebrush, { skipAnalytics: true });
      if (removeLineItem) await removeLineItem(editLineItem);
    }

    router.push('/cart');
  };

  const handleAddToCart = async () => {
    if (!quantity || isCartItemEditPending || hasUnresolvedCartFinish) return;

    setLoading(true);

    try {
      if (isEditingCartItem) {
        await handleUpdateCartItem();
        setLoading(false);
        setAdded(true);
        return;
      }

      const cart = await onAddToCart?.(
        variant,
        quantity,
        finish?.name,
        sheen,
        hasWirebrush,
      );

      setLoading(false);
      setAdded(true);

      const matchingItem = cart
        ? findMatchingLineItem(cart, variant, {
          finish,
          sheen,
          hasWirebrush,
        })
        : null;

      const rawBasePrice = variant.price?.centAmount || 0;
      const rawDiscountedPrice = variant.discountedPrice?.centAmount || 0;

      // Calculate discount factor (e.g. 0.8 for 20% off)
      const discountFactor =
        rawBasePrice > 0 && rawDiscountedPrice > 0 ? rawDiscountedPrice / rawBasePrice : 1;

      const finishPrice = finish?.price || 0;
      const discountedFinishPrice = Math.round(finishPrice * discountFactor);

      // Create a cloned variant with updated prices for the overlay only
      const displayVariant = {
        ...variant,
        price: variant.price
          ? {
            ...variant.price,
            centAmount: rawBasePrice + finishPrice,
          }
          : undefined,
        discountedPrice: variant.discountedPrice
          ? {
            ...variant.discountedPrice,
            centAmount: rawDiscountedPrice + discountedFinishPrice,
          }
          : undefined,
      };

      show(product, displayVariant as ExtendedVariant, quantity, {
        // Hand the overlay the exact line we just resolved — its own SKU + config search cannot
        // tell two externally-configured lines apart.
        lineItemId: matchingItem?.lineItemId,
        customLineItemId: matchingItem?.customLineItemId,
        config: matchingItem?.config || {
          finish: finish?.name,
          sheen,
          wirebrush: hasWirebrush,
        },
      });

      setTimeout(() => {
        setAdded(false);
        setQuantity(1);
      }, 1000);
    } catch (error) {
      console.error('Error adding item to cart:', error);
      setLoading(false);
    }
  };

  /* ================= Layout Classes ================= */

  const wrapperClassName = inModalVersion
    ? 'container md:grid md:grid-cols-12 gap-10 py-12'
    : 'container grid grid-cols-12 gap-y-12 md:gap-x-16 py-12 items-start';

  const galleryContainerClassName = useClassNames([
    inModalVersion
      ? 'col-span-12 md:col-span-6'
      : 'col-span-12 md:col-span-6 lg:col-span-7',
  ]);

  const informationContainerClassName = useClassNames([
    inModalVersion
      ? 'col-span-12 md:col-span-6'
      : 'col-span-12 md:col-span-6 lg:col-span-5 md:ml-16',
  ]);

  const categoryHasCustomQuoteCta = hasCustomQuoteCta(
    product?.categories || [],
  );

  const ctaContainerClassName = useClassNames([
    'mt-8 mb-24 flex items-center gap-9',
    categoryHasCustomQuoteCta ? 'flex-wrap' : '',
  ]);

  // Mobile: show a persistent bottom strip with the Discovery Kit CTA while the on-page CTA
  // buttons are off-screen; hide it once they scroll into view.
  const ctaSectionRef = useRef<HTMLDivElement>(null);
  const [showStickyKit, setShowStickyKit] = useState(false);
  useEffect(() => {
    const cta = ctaSectionRef.current;
    if (!cta || inModalVersion || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(([entry]) => setShowStickyKit(!entry.isIntersecting), {
      threshold: 0,
    });
    observer.observe(cta);
    return () => observer.disconnect();
  }, [inModalVersion]);

  return (
    <>
      <div className={wrapperClassName}>
        {/* Gallery */}
        <div className={galleryContainerClassName}>
          <Gallery
            key={variant.sku}
            images={
              product.images.length
                ? product.images
                : variant.images?.map((img) => ({ src: img })) ?? []
            }
            inModalVersion={inModalVersion}
            enableImageModal
          />
        </div>

        {/* Product Info */}
        <div className={informationContainerClassName}>
          <ProductInformation
            reviewListProps={reviewListProps}
            product={product}
            variant={variant}
            plainTextDescription={plainTextDescription}
            description={description}
            category={category}
            onChangeVariant={onChangeVariant}
            inModalVersion={inModalVersion}
            origin={origin}
            categoryCtaText={categoryCtaText}
            categoryCtaUrl={categoryCtaUrl}
            extraCentAmount={finish?.price}
          />

          {/* CTA Section */}
          <div ref={ctaSectionRef} className={ctaContainerClassName}>
            {variant.price?.centAmount ? (
              <>
                <Link
                  buttonVariant="secondary-black"
                  className="block w-full !border-primary-black !bg-white text-center font-semibold !text-primary-black hover:!bg-neutral-100"
                  link="/flight-discovery-kit/p/900023/"
                >
                  Get a FLIGHT Discovery Kit &gt;
                </Link>
                <Link
                  buttonVariant="secondary-black"
                  className="block w-full text-center font-semibold"
                  link={customQuoteLink}
                  target="_blank"
                >
                  Request a Quote &gt;
                </Link>
                <Button
                  variant="ghost"
                  size="fit"
                  className="w-full text-left !text-14 !font-normal text-link underline underline-offset-2 hover:!text-blue-dark"
                  onClick={handleAddToCart}
                  loading={loading}
                  added={added}
                  disabled={isCartItemEditPending || hasUnresolvedCartFinish}
                >
                  {isCartItemEditPending || hasUnresolvedCartFinish
                    ? formatMessage({ id: 'cart.item.loading', defaultMessage: 'Loading cart item...' })
                    : isEditingCartItem
                    ? formatMessage({ id: 'cart.update', defaultMessage: 'Update cart' })
                    : 'Buy as Pictured'}
                </Button>
              </>
            ) : (
              <div className="flex flex-col gap-4">
                <p className="text-sm font-medium text-error">
                  Product currently unavailable, please contact our Sales team.
                </p>
                <Link
                  buttonVariant="secondary"
                  className="flex justify-center"
                  link="/contact-us"
                  target="_blank"
                >
                  Contact Us
                </Link>
              </div>
            )}
          </div>

          {inModalVersion && (
            <Link
              link={url}
              className="mx-auto mt-8 block w-fit text-sm text-secondary-black hover:underline"
              onClick={() => setIsOpen?.(false)}
            >
              {formatMessage({
                id: 'more.details',
                defaultMessage: 'More details',
              })}
            </Link>
          )}
        </div>

        {!inModalVersion && productContent && (
          <AdditionalInfo {...productContent} />
        )}
      </div>

      {/* Mobile persistent Discovery Kit strip — visible only while the on-page CTAs are off-screen. */}
      {!inModalVersion && variant.price?.centAmount && (
        <div
          aria-hidden={!showStickyKit}
          className={`fixed inset-x-0 bottom-0 z-40 border-t border-stroke-primary bg-white px-16 pt-12 shadow-sm transition-transform duration-200 md:hidden ${
            showStickyKit ? 'translate-y-0' : 'pointer-events-none translate-y-full'
          }`}
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
        >
          <Link
            buttonVariant="secondary-black"
            className="block w-full text-center font-semibold"
            link="/flight-discovery-kit/p/900023/"
            tabIndex={showStickyKit ? undefined : -1}
          >
            Get a FLIGHT Discovery Kit &gt;
          </Link>
        </div>
      )}
    </>
  );
};

export default CustomProductDetails;
